/**
 * Time-range presets for the AI Costs page.
 *
 * Computes {from, to} as YYYY-MM-DD strings, client-side, from `new Date()`
 * (passed in as `now` for testability). Passed straight to every /costs endpoint
 * as from&to. `to` is always today; `from` is the preset's lookback / period
 * start. 'custom' is handled by the page (user-supplied from & to), not here.
 */

export type TimePreset = '1D' | '7D' | '1M' | '3M' | 'QTD' | 'YTD' | '1Y';

/**
 * Ordered pills for the shared `TimeRangeFilter`. Custom is NOT in this list —
 * the filter renders its own Custom pill, and the page resolves it through the
 * value's own from/to rather than through `computeRange`.
 *
 * These tokens are period starts (QTD/YTD/1Y), not calendar-day lookbacks, so
 * the shared `resolveRange()` cannot speak them. The page keeps `computeRange`.
 */
export const COST_PRESETS: { token: TimePreset; label: string }[] = [
  { token: '1D', label: '1D' },
  { token: '7D', label: '7D' },
  { token: '1M', label: '1M' },
  { token: '3M', label: '3M' },
  { token: 'QTD', label: 'QTD' },
  { token: 'YTD', label: 'YTD' },
  { token: '1Y', label: '1Y' },
];

/** Format a Date as a local YYYY-MM-DD (no UTC shift). */
export function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Resolve a preset to a {from, to} date-string range. An unknown token returns
 * the current day as a safe fallback.
 */
export function computeRange(preset: TimePreset, now: Date = new Date()): { from: string; to: string } {
  const to = new Date(now);
  const from = new Date(now);

  switch (preset) {
    case '1D':
      from.setDate(from.getDate() - 1);
      break;
    case '7D':
      from.setDate(from.getDate() - 7);
      break;
    case '1M':
      from.setMonth(from.getMonth() - 1);
      break;
    case '3M':
      from.setMonth(from.getMonth() - 3);
      break;
    case 'QTD': {
      const qStartMonth = Math.floor(now.getMonth() / 3) * 3;
      from.setMonth(qStartMonth, 1);
      break;
    }
    case 'YTD':
      from.setMonth(0, 1);
      break;
    case '1Y':
      from.setFullYear(from.getFullYear() - 1);
      break;
    default:
      break;
  }

  return { from: ymd(from), to: ymd(to) };
}
